'use client';

import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { BlogSection } from '../data/blogs';
import BlogShareLinks from './BlogShareLinks';
import BlogTableOfContents from './BlogTableOfContents';

gsap.registerPlugin(ScrollTrigger);

interface BlogPostContentGridProps {
  sections: BlogSection[];
  shareUrl: string;
  shareTitle: string;
  children: ReactNode;
}

function getHeaderOffset() {
  const header = document.getElementById('site-header');
  if (header) return header.getBoundingClientRect().height;

  const cssValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--site-header-height')
    .trim();
  const parsedValue = Number.parseFloat(cssValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

export default function BlogPostContentGrid({
  sections,
  shareUrl,
  shareTitle,
  children,
}: BlogPostContentGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const sidebar = sidebarRef.current;
    if (!grid || !sidebar) return;

    const mm = gsap.matchMedia();

    mm.add('(min-width: 1024px)', () => {
      const trigger = ScrollTrigger.create({
        trigger: grid,
        start: () => `top top+=${getHeaderOffset() + 24}`,
        end: 'bottom bottom',
        pin: sidebar,
        pinSpacing: false,
        invalidateOnRefresh: true,
        anticipatePin: 1,
      });

      return () => {
        trigger.kill();
        gsap.set(sidebar, { clearProps: 'all' });
      };
    });

    const refreshId = window.requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      window.cancelAnimationFrame(refreshId);
      mm.revert();
    };
  }, [sections.length]);

  return (
    <div
      ref={gridRef}
      className="mt-6 grid gap-7 sm:mt-8 lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start lg:gap-10"
    >
      <div ref={sidebarRef} className="self-start max-sm:hidden">
        <BlogTableOfContents sections={sections} shareUrl={shareUrl} shareTitle={shareTitle} />
      </div>

      <div className="w-full">
        {children}

        <div className="mt-8 sm:hidden">
          <BlogShareLinks
            url={shareUrl}
            title={shareTitle}
            className="rounded-2xl bg-white p-4 shadow-[0_18px_45px_-32px_rgba(0,0,0,0.45)] sm:p-5"
          />
        </div>
      </div>
    </div>
  );
}
